// src/pages/shop/ShopAiTools.jsx
//
// Главный экран AI инструментов магазина.
// Показывает:
//   - текущий лимит
//   - кнопку «Сгенерировать новое» (открывает AiGenerateSheet без привязки к товару)
//   - последние 10 генераций
//   - ссылку на полную историю
//
// Сюда попадают:
//   - из быстрых действий на ShopHome
//   - из бокового меню (если кто-то добавит)

import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ai as aiApi } from '../../api';
import { useToast } from '../../hooks/useToast';
import AiGenerateSheet from '../../components/AiGenerateSheet';
import Icon from '../../components/Icon';

const API_HOST = (() => {
  try {
    const u = new URL(process.env.REACT_APP_API_URL || 'https://api.alsatuu.biz/api');
    return `${u.protocol}//${u.host}`;
  } catch { return 'https://api.alsatuu.biz'; }
})();
function fullUrl(u) {
  if (!u) return '';
  if (u.startsWith('http')) return u;
  return API_HOST + u;
}

export default function ShopAiTools() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { show, ToastView } = useToast();
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aiOpen, setAiOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      aiApi.status().catch(() => null),
      aiApi.history(10).catch(() => ({ items: [] })),
    ]).then(([s, h]) => {
      setStatus(s);
      setHistory(h.items || []);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const source    = status?.source;       // 'trial' | 'addon' | null
  const allowed   = status?.allowed;
  const trialLeft = status?.trialLeft || 0;
  const limit = status?.limit;
  const used  = status?.used || 0;
  const ratio = (source === 'addon' && limit) ? used / limit : 0;
  const barColor =
    ratio >= 0.9 ? 'var(--danger)' :
    ratio >= 0.7 ? '#f59e0b' :
    'var(--brand-primary)';

  return (
    <div className="page" style={{ padding: 20 }}>
      {/* Шапка */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <button className="back-btn" onClick={() => navigate(-1)}>
          <Icon name="chevronLeft" size={18} />
        </button>
        <h1 style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', flex: 1 }}>
          {t('shopAi.title')}
        </h1>
      </div>

      <p style={{ fontSize: 13, color: 'var(--fg-muted)', marginBottom: 20 }}>
        {t('shopAi.subtitle')}
      </p>

      {/* Виджет лимита */}
      {status && (
        <div style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--r-md)',
          padding: 16,
          marginBottom: 16,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: 'var(--fg-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
              {source === 'trial' ? t('shopAi.trialLabel')
                : source === 'addon' ? t('shopAi.usedThisPeriod')
                : t('shopAi.noAccessLabel')}
            </span>
            <span style={{ fontSize: 14, fontWeight: 700, color: source === 'addon' ? barColor : (allowed ? 'var(--brand-primary)' : 'var(--fg-muted)') }}>
              {source === 'trial' ? t('shopAi.trialLeft', { count: trialLeft })
                : source === 'addon' ? `${used} / ${limit}`
                : '—'}
            </span>
          </div>

          {source === 'addon' && limit ? (
            <div style={{ height: 6, background: 'var(--bg-subtle)', borderRadius: 3, overflow: 'hidden' }}>
              <div style={{ width: `${Math.min(100, ratio * 100)}%`, height: '100%', background: barColor, transition: 'width 0.3s' }} />
            </div>
          ) : null}

          {source === 'trial' && (
            <p style={{ fontSize: 11, color: 'var(--fg-faint)', marginTop: 8 }}>{t('shopAi.trialHint')}</p>
          )}
          {source === 'addon' && status.resetAt && (
            <p style={{ fontSize: 11, color: 'var(--fg-faint)', marginTop: 8 }}>
              {t('shopAi.resetAt')} {new Date(status.resetAt).toLocaleDateString()}
            </p>
          )}
          {!allowed && (
            <p style={{ fontSize: 12, color: 'var(--fg-muted)', marginTop: 8 }}>{t('shopAi.noAccessHint')}</p>
          )}
        </div>
      )}

      {/* Основная кнопка */}
      <button
        onClick={() => setAiOpen(true)}
        style={{
          width: '100%',
          padding: '16px 20px',
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          color: '#fff',
          border: 'none',
          borderRadius: 'var(--r-md)',
          fontSize: 16, fontWeight: 700,
          cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
          marginBottom: 16,
        }}
      >
        <Icon name="sparkles" size={20} />
        {t('shopAi.startNew')}
      </button>

      {/* История */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <h3 style={{ fontSize: 13, color: 'var(--fg-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
          {t('shopAi.recent')}
        </h3>
        {history.length >= 10 && (
          <button
            onClick={() => navigate('/ai-history')}
            style={{ background: 'none', border: 'none', color: 'var(--brand-primary)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
          >
            {t('shopAi.viewAll')} →
          </button>
        )}
      </div>

      {loading && <div className="loader" style={{ padding: 24 }}><div className="spinner" /></div>}

      {!loading && history.length === 0 && (
        <div style={{ textAlign: 'center', padding: 32, color: 'var(--fg-muted)' }}>
          <Icon name="sparkles" size={36} style={{ opacity: 0.3, marginBottom: 8 }} />
          <p style={{ fontSize: 13 }}>{t('shopAi.empty')}</p>
        </div>
      )}

      {!loading && history.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
          {history.map(g => (
            <button
              key={g.id}
              onClick={() => navigate(`/ai-history?focus=${g.id}`)}
              style={{
                aspectRatio: '3/4',
                background: 'var(--bg-subtle)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--r-sm)',
                overflow: 'hidden',
                position: 'relative',
                padding: 0,
                cursor: 'pointer',
              }}
            >
              {g.outputUrl && g.status === 'success' ? (
                <img
                  src={fullUrl(g.outputUrl)}
                  alt=""
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <div style={{
                  width: '100%', height: '100%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: g.status === 'failed' ? 'var(--danger)' : 'var(--fg-muted)',
                  fontSize: 11,
                }}>
                  {g.status === 'failed' ? t('shopAi.failed') : t('shopAi.processing')}
                </div>
              )}
              <div style={{
                position: 'absolute', bottom: 0, left: 0, right: 0,
                background: 'rgba(0,0,0,0.6)',
                color: '#fff', fontSize: 10, padding: '3px 6px',
                textAlign: 'center',
              }}>
                {t(`aiGenerate.type${g.type === 'try_on' ? 'TryOn' : g.type === 'studio_photo' ? 'Studio' : 'Pedestal'}`)}
              </div>
            </button>
          ))}
        </div>
      )}

      <AiGenerateSheet
        open={aiOpen}
        onClose={() => { setAiOpen(false); load(); }}
        onApply={(url) => {
          show(t('shopAi.savedToHistory'), 'success');
          load();
        }}
        onAddToGallery={(url) => {
          show(t('shopAi.savedToHistory'), 'success');
          load();
        }}
      />

      {ToastView}
    </div>
  );
}
